// One question, rendered — for filling in, or read-only for looking back at.
//
// ONE SWITCH, not two components. The read-only view of a form has to show
// exactly what the fill view showed, including which checklist options were
// NOT ticked, and two components drift the moment one of them is changed. The
// mode falls out of a prop rather than out of a separate file.
//
// Read-only is a RECORD, not a greyed-out form (the 2026-09-29 restyle): a
// disabled input reads as a question nobody answered, so the stated types
// (text, number, date, entity, meters) render their answer as plain text and
// an unanswered question says "Not answered." out loud. Choice and checklist
// keep every option on screen with the picked ones marked, because what was
// not chosen is part of the record — the same reason the PDF prints them.
//
// Grouped questions (options, photos, signature, meters) are labelled with
// `role` + `aria-labelledby` rather than `<label htmlFor>`: the old label
// pointed at an id no element had, which left screen readers a floating
// label and an unnamed group.
import { useRef, useState } from 'react';
import type { FormField, SubmissionFile } from '../../formsApi';
import { compressImage } from '../../lib/compressImage';
import { SignaturePad } from './SignaturePad';
import { FileDrop } from '../ui/FileDrop';
import { serverFileSource, type FormFileSource } from './fileSource';

export interface MeterOption {
  id: string;
  label: string;
  unit: string;
  currentReading: number | null;
}

/**
 * Yes / No / N/A and its kin render as ONE row of equal segments instead of
 * stacked rows: 2 or 3 options, every label this short. The threshold is
 * deterministic on purpose — the builder's preview and the fill screen are
 * the same component, and a heuristic that could disagree with itself would
 * make the preview a lie. Pinned by a test.
 */
const SEGMENT_MAX_OPTIONS = 3;
const SEGMENT_MAX_LABEL = 10;

function segmentsFit(options: string[]): boolean {
  return (
    options.length >= 2 &&
    options.length <= SEGMENT_MAX_OPTIONS &&
    options.every((option) => option.length <= SEGMENT_MAX_LABEL)
  );
}

/** 'YYYY-MM-DD' read back the Australian way. By hand, never through Date:
    a calendar date reinterpreted as an instant can land on the day before. */
function formatDateOnly(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : value;
}

function cls(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/** The record's honest reading of a question with nothing in it. */
function NotAnswered() {
  return <p className="muted">Not answered.</p>;
}

/**
 * A number field that a person can actually type into.
 *
 * THE OBVIOUS VERSION DOES NOT WORK, and this app shipped it. Storing
 * `Number(e.target.value)` and rendering `String(value)` back means the input
 * is re-rendered from the parsed number on every keystroke — so the moment you
 * type "12." it becomes "12", the decimal point is eaten, and a reading like
 * 123.5 engine hours is literally untypeable. Any transient non-number ("1,234"
 * mid-edit) parses to NaN and is rendered back as the four letters "NaN".
 *
 * So the RAW TEXT is what lives in state while the field has focus, and the
 * parsed number is what goes up — the same split `BulkReadingsPage` uses, which
 * is why meters could always be typed on the yard walk but not on a prestart.
 * A value that arrives from outside (a loaded draft) re-seeds the text, unless
 * the text already means that number — otherwise "12." would be rewritten to
 * "12" by the round trip we are trying to avoid.
 */
function NumericInput({
  id,
  value,
  disabled,
  className,
  placeholder,
  describedBy,
  invalid,
  onValue,
}: {
  id?: string;
  value: number | undefined;
  disabled: boolean;
  className: string;
  placeholder?: string;
  describedBy?: string;
  invalid?: boolean;
  onValue: (value: number | undefined) => void;
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value));
  // What we last sent up. Anything else arriving in `value` came from OUTSIDE
  // — a draft loading, a form being reset — and should replace what is typed.
  const ours = useRef<number | undefined>(value);

  if (value !== ours.current) {
    ours.current = value;
    setText(value === undefined ? '' : String(value));
  }

  const push = (next: number | undefined): void => {
    ours.current = next;
    onValue(next);
  };

  return (
    <input
      id={id}
      // `decimal`, not `number`: the numeric keypad without the spinner and
      // the scroll-wheel-changes-the-value trap.
      inputMode="decimal"
      className={className}
      disabled={disabled}
      placeholder={placeholder}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        if (raw.trim() === '') {
          push(undefined);
          return;
        }
        const parsed = Number(raw);
        // Half-typed input ("12.", "-", "1e") holds the last good value rather
        // than pushing NaN into the answers and onto the PDF.
        if (Number.isFinite(parsed)) push(parsed);
      }}
    />
  );
}

export function FormFieldInput({
  field,
  value,
  files,
  meters,
  refOptions,
  submissionId,
  readOnly,
  problem,
  signedCaption,
  onChange,
  onFilesChanged,
  fileSource,
}: {
  field: FormField;
  value: unknown;
  files: SubmissionFile[];
  /** The linked machine's meters. Empty unless this is an asset form. */
  meters: MeterOption[];
  /**
   * A "Pick from the app" question's dropdown, fetched by the PAGE (the
   * meters precedent — this component stays dumb). Absent while loading and
   * on read-only renders, where the stored label is the record anyway.
   */
  refOptions?: { id: string; label: string }[];
  submissionId: string;
  readOnly: boolean;
  /**
   * The refused-submit marker (round 2026-09-29): a short sentence rendered
   * under the field with the control marked invalid. Computed by the page,
   * live, so answering the question clears its own marker.
   */
  problem?: string | null;
  /** The read-back's who-and-when line under a signature. */
  signedCaption?: string | null;
  onChange: (value: unknown) => void;
  onFilesChanged: () => void;
  /**
   * Where photos come from and go to (round 42). The fill page passes one
   * that answers from the phone first; everything else reads the server.
   */
  fileSource?: FormFileSource;
}) {
  const source = fileSource ?? serverFileSource(submissionId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mine = files.filter((f) => f.fieldKey === field.key);
  const id = `field-${field.key}`;
  const labelId = `${id}-label`;
  const problemId = problem ? `${id}-problem` : undefined;

  async function upload(file: File | Blob, filename: string, kind: 'photo' | 'signature') {
    setBusy(true);
    setError(null);
    try {
      const uploaded = await source.upload(field.key, kind, file, filename);
      // The ANSWER holds the id; the file row holds the bytes. Updating both
      // here keeps them in step without a round trip to re-read the form.
      if (kind === 'photo') {
        const current = Array.isArray(value) ? (value as string[]) : [];
        onChange([...current, uploaded.id]);
      } else {
        onChange(uploaded.id);
      }
      onFilesChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not upload.');
    } finally {
      setBusy(false);
    }
  }

  async function removeFile(fileId: string) {
    setBusy(true);
    setError(null);
    try {
      await source.remove(fileId);
      if (field.type === 'photo') {
        const current = Array.isArray(value) ? (value as string[]) : [];
        onChange(current.filter((x) => x !== fileId));
      } else {
        onChange(undefined);
      }
      onFilesChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that.');
    } finally {
      setBusy(false);
    }
  }

  // Grouped types have no single control a <label> could point at; the
  // heading is a span the group references by id.
  const grouped =
    field.type === 'choice' ||
    field.type === 'checklist' ||
    field.type === 'photo' ||
    field.type === 'signature' ||
    field.type === 'meter_reading';

  const heading = grouped ? (
    <span id={labelId} className="form-label">
      {field.label || 'Question'}
      {field.required && <span className="ml-1 text-red-600">*</span>}
    </span>
  ) : (
    <label className="form-label" htmlFor={id}>
      {field.label || 'Question'}
      {field.required && <span className="ml-1 text-red-600">*</span>}
    </label>
  );

  const body = (() => {
    switch (field.type) {
      case 'text':
        if (readOnly) {
          return typeof value === 'string' && value !== '' ? (
            <p className="whitespace-pre-wrap text-sm text-gray-900">{value}</p>
          ) : (
            <NotAnswered />
          );
        }
        return field.multiline ? (
          <textarea
            id={id}
            className={cls('form-input', problem && 'form-input-error')}
            rows={3}
            aria-describedby={problemId}
            aria-invalid={problem ? true : undefined}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            id={id}
            className={cls('form-input', problem && 'form-input-error')}
            aria-describedby={problemId}
            aria-invalid={problem ? true : undefined}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 'number':
        if (readOnly) {
          return typeof value === 'number' ? (
            <p className="text-sm text-gray-900">
              {value.toLocaleString('en-AU')}
              {field.unit ? ` ${field.unit}` : ''}
            </p>
          ) : (
            <NotAnswered />
          );
        }
        return (
          <div className="flex items-center gap-2">
            <NumericInput
              id={id}
              className={cls('form-input', problem && 'form-input-error')}
              disabled={false}
              describedBy={problemId}
              invalid={Boolean(problem)}
              value={typeof value === 'number' ? value : undefined}
              onValue={onChange}
            />
            {field.unit && <span className="text-sm text-gray-600">{field.unit}</span>}
          </div>
        );

      case 'date':
        if (readOnly) {
          return typeof value === 'string' && value !== '' ? (
            <p className="text-sm text-gray-900">{formatDateOnly(value)}</p>
          ) : (
            <NotAnswered />
          );
        }
        return (
          <input
            id={id}
            type="date"
            className={cls('form-input', problem && 'form-input-error')}
            aria-describedby={problemId}
            aria-invalid={problem ? true : undefined}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value || undefined)}
          />
        );

      case 'choice': {
        // Every option visible is the point on a prestart: what was NOT
        // chosen is part of the record, and it reads the same here as it
        // does on the PDF. Short sets sit as segments, the rest as rows —
        // see segmentsFit above.
        const options = field.options ?? [];
        const picked = typeof value === 'string' ? value : undefined;
        const groupProps = {
          role: 'radiogroup' as const,
          'aria-labelledby': labelId,
          'aria-required': field.required || undefined,
          'aria-describedby': problemId,
        };

        const clearAnswer =
          !field.required && !readOnly && picked !== undefined ? (
            // A radio group cannot be un-picked, so an OPTIONAL question
            // needs a way back out of an answer given by mistake (ZT's
            // affordance, kept with its reasoning).
            <button
              type="button"
              className="row-action-muted mt-1"
              onClick={() => onChange(undefined)}
            >
              Clear answer
            </button>
          ) : null;

        if (segmentsFit(options)) {
          return (
            <div>
              <div className="option-segments" {...groupProps}>
                {options.map((option) => (
                  <label
                    key={option}
                    className={cls(
                      'option-segment',
                      picked === option && 'option-segment-selected',
                      readOnly && 'option-segment-static'
                    )}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      name={id}
                      disabled={readOnly}
                      checked={picked === option}
                      onChange={() => onChange(option)}
                    />
                    {option}
                  </label>
                ))}
              </div>
              {clearAnswer}
              {readOnly && picked === undefined && (
                <p className="muted mt-1">Not answered.</p>
              )}
            </div>
          );
        }

        return (
          <div>
            <div className="space-y-2" {...groupProps}>
              {options.map((option) => (
                <label
                  key={option}
                  className={cls(
                    'option-row',
                    picked === option && 'option-row-selected',
                    readOnly && 'option-row-static'
                  )}
                >
                  <input
                    type="radio"
                    className="form-check mt-0.5"
                    name={id}
                    disabled={readOnly}
                    checked={picked === option}
                    onChange={() => onChange(option)}
                  />
                  <span className="min-w-0">{option}</span>
                </label>
              ))}
            </div>
            {clearAnswer}
            {readOnly && picked === undefined && <p className="muted mt-1">Not answered.</p>}
          </div>
        );
      }

      case 'checklist': {
        const options = field.options ?? [];
        const picked = Array.isArray(value) ? (value as string[]) : [];
        // Never segments: a row of multi-select segments reads as a set of
        // independent toggles, which is a different question. And no "Not
        // answered." on the read-back — nothing ticked can be a real answer.
        return (
          <div
            className="space-y-2"
            role="group"
            aria-labelledby={labelId}
            aria-describedby={problemId}
          >
            {options.map((option) => (
              <label
                key={option}
                className={cls(
                  'option-row',
                  picked.includes(option) && 'option-row-selected',
                  readOnly && 'option-row-static'
                )}
              >
                <input
                  type="checkbox"
                  className="form-check mt-0.5"
                  disabled={readOnly}
                  checked={picked.includes(option)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...picked, option]
                        : picked.filter((o) => o !== option)
                    )
                  }
                />
                <span className="min-w-0">{option}</span>
              </label>
            ))}
          </div>
        );
      }

      case 'photo': {
        const max = field.max_photos ?? 10;
        return (
          <div role="group" aria-labelledby={labelId} aria-describedby={problemId}>
            {mine.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {mine.map((file) => (
                  <div key={file.id} className="relative">
                    {/* The thumbnail opens the real photo: a manager reading
                        a defect report needs the fault, not a 96px square. */}
                    <a
                      href={source.url(file.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                      aria-label="Open the photo full size"
                    >
                      <img
                        src={source.url(file.id)}
                        alt=""
                        className="h-24 w-24 rounded border border-gray-200 object-cover"
                      />
                    </a>
                    {!readOnly && (
                      // Drawn at 28px, touched at 44px via the after inset —
                      // the one sanctioned way under the floor, and thumbs
                      // sit 104px apart so two hit areas cannot overlap
                      // (measured in the browser, not assumed).
                      <button
                        type="button"
                        className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 shadow-sm transition-colors after:absolute after:-inset-2 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 disabled:opacity-50"
                        aria-label="Remove this photo"
                        disabled={busy}
                        onClick={() => void removeFile(file.id)}
                      >
                        <svg
                          viewBox="0 0 20 20"
                          className="h-3.5 w-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {readOnly && mine.length === 0 && <p className="muted">No photos.</p>}
            {!readOnly && mine.length < max && (
              <div className={mine.length > 0 ? 'mt-3' : ''}>
                {/* A button over a hidden input, not the bare native control
                    (round 33's rule). `capture` and `accept` stay ON THE INPUT
                    inside FileDrop, so tapping this still opens the camera
                    directly, which is the whole point for somebody standing at
                    a machine; on a computer a photo can be dropped on it
                    (round 45). */}
                <FileDrop
                  variant="button"
                  id={id}
                  accept="image/*"
                  capture="environment"
                  typeLabel="photos"
                  buttonClassName="btn-secondary"
                  disabled={busy}
                  label={busy ? 'Adding…' : 'Take or choose a photo'}
                  onFiles={(files) => {
                    const file = files[0];
                    if (!file) return;
                    void (async () => {
                      // Compressed BEFORE upload — see lib/compressImage.ts.
                      const compressed = await compressImage(file);
                      await upload(compressed, file.name || 'photo.jpg', 'photo');
                    })();
                  }}
                />
                <span className="hint">
                  {mine.length} of {max}. Photos are shrunk before they are sent, so they go
                  through on a bad signal.
                </span>
              </div>
            )}
          </div>
        );
      }

      case 'signature': {
        const signature = mine[0];
        if (signature) {
          return (
            <div role="group" aria-labelledby={labelId}>
              <img
                src={source.url(signature.id)}
                alt="Signature"
                className="h-24 rounded border border-gray-200 bg-white"
              />
              {readOnly && signedCaption && <p className="form-hint">{signedCaption}</p>}
              {!readOnly && (
                <button
                  type="button"
                  className="row-action-muted mt-1"
                  disabled={busy}
                  onClick={() => void removeFile(signature.id)}
                >
                  Clear and sign again
                </button>
              )}
            </div>
          );
        }
        if (readOnly) return <p className="muted">Not signed.</p>;
        return (
          <div role="group" aria-labelledby={labelId} aria-describedby={problemId}>
            <SignaturePad
              disabled={busy}
              onSigned={(png) => void upload(png, 'signature.png', 'signature')}
            />
          </div>
        );
      }

      case 'meter_reading': {
        const entries = Array.isArray(value)
          ? (value as { meter_id: string; reading: number }[])
          : [];
        if (meters.length === 0) {
          return <p className="muted">This machine has no meters set up.</p>;
        }
        if (readOnly) {
          return (
            <div className="space-y-1">
              {meters.map((meter) => {
                const entry = entries.find((e) => e.meter_id === meter.id);
                return (
                  <p key={meter.id} className="text-sm text-gray-900">
                    {meter.label}:{' '}
                    {entry ? (
                      `${entry.reading.toLocaleString('en-AU')} ${meter.unit}`
                    ) : (
                      <span className="text-gray-600">not read</span>
                    )}
                  </p>
                );
              })}
            </div>
          );
        }
        return (
          <div
            className="space-y-2"
            role="group"
            aria-labelledby={labelId}
            aria-describedby={problemId}
          >
            {meters.map((meter) => {
              const entry = entries.find((e) => e.meter_id === meter.id);
              // A reading below the last one still submits: the operator is
              // at the dial, and a meter can be replaced. It is just never
              // silent (meterReadings.ts' regression rule), because a typo
              // here drops a due service out of the reminders.
              const lower =
                entry !== undefined &&
                meter.currentReading !== null &&
                entry.reading < meter.currentReading;
              const lowerId = `${id}-${meter.id}-lower`;
              return (
                <div key={meter.id} className="flex flex-wrap items-center gap-2">
                  <span className="w-32 text-sm text-gray-700">{meter.label}</span>
                  <NumericInput
                    className="form-input w-40"
                    disabled={false}
                    placeholder={
                      meter.currentReading === null
                        ? 'Never read'
                        : `Last: ${meter.currentReading.toLocaleString('en-AU')}`
                    }
                    value={entry ? entry.reading : undefined}
                    describedBy={lower ? lowerId : undefined}
                    onValue={(reading) => {
                      const others = entries.filter((x) => x.meter_id !== meter.id);
                      // A blank secondary meter is normal — the person only
                      // reads the one they can see — so it is dropped rather
                      // than stored as zero.
                      onChange(
                        reading === undefined
                          ? others
                          : [...others, { meter_id: meter.id, reading }]
                      );
                    }}
                  />
                  <span className="text-sm text-gray-600">{meter.unit}</span>
                  {lower && (
                    <p id={lowerId} role="status" className="!mb-0 w-full text-sm text-amber-900">
                      That is lower than the last reading of{' '}
                      {meter.currentReading!.toLocaleString('en-AU')} {meter.unit}. Check the dial;
                      if it is right, you can still submit.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      case 'entity_ref': {
        const picked = (value ?? null) as { id: string; label?: string } | null;
        if (readOnly) {
          // The STORED label, as text — rebuilt from the row at submit, so
          // this reads what the thing was called the day the form was
          // signed. A greyed select would read as a question somebody failed
          // to answer.
          return picked?.label ? (
            <p className="text-sm text-gray-900">{picked.label}</p>
          ) : (
            <NotAnswered />
          );
        }
        const options = refOptions ?? [];
        return (
          <select
            id={id}
            className={cls('form-select', problem && 'form-input-error')}
            aria-describedby={problemId}
            aria-invalid={problem ? true : undefined}
            disabled={options.length === 0 && !picked}
            value={picked?.id ?? ''}
            onChange={(e) => {
              const option = options.find((o) => o.id === e.target.value);
              // The label rides along for the SCREEN; the server throws it
              // away at submit and rebuilds it from the row.
              onChange(option ? { id: option.id, label: option.label } : undefined);
            }}
          >
            <option value="">
              {options.length === 0 ? 'Loading…' : 'Choose…'}
            </option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
            {/* A pick whose option has since left the list still shows. */}
            {picked && !options.some((o) => o.id === picked.id) && (
              <option value={picked.id}>{picked.label ?? 'Current pick'}</option>
            )}
          </select>
        );
      }

      default:
        return null;
    }
  })();

  return (
    // The wrapper id is the scroll target for the refused-submit markers.
    <div className="mb-4" id={`q-${field.key}`}>
      {heading}
      {body}
      {problem && (
        <p id={problemId} className="form-error">
          {problem}
        </p>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
